import styled, { css } from 'styled-components';

import { borderGradientStyle } from '#src/style/utils.tsx';

// https://www.figma.com/design/SFAfhEiMKvudU66FU9MB8G/Components?node-id=8212-10873
type ChipColor =
  | 'default'
  | 'black'
  | 'transparent'
  | 'yellow'
  | 'orange'
  | 'red'
  | 'blue'
  | 'green'
  | 'purple'
  | 'teal'
  | 'light'
  | 'glow';

type StyleProps = {
  color?: ChipColor;
  /** Matches the Figma sizes: M is 24px, S is 20px, XS is 12px. */
  size?: 'M' | 'S' | 'XS';
};

const wash = (accent: string) =>
  `color-mix(in srgb, ${accent} 16%, var(--c-dark-primary))`;

const stroke = (from: string, via: string, to: string, viaStop = '76.9%') =>
  `linear-gradient(to top, ${from} 0%, ${via} ${viaStop}, ${to} 100%)`;

export const Chip = styled.span<StyleProps>`
  --chip-glow-size: 9px;
  --chip-text-color: var(--c-white);
  --border-size: 1px;

  ${borderGradientStyle};
  flex-shrink: 0;
  display: inline-flex;
  gap: var(--half-unit);
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 100px;
  background: var(--chip-surface-color);
  box-shadow: inset 0 0 var(--chip-glow-size) 0 var(--chip-glow-color);
  font: var(--t-brand-xxs);
  color: var(--chip-text-color);
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;

  ${({ color = 'default' }) => {
    switch (color) {
      case 'black':
        return css`
          --border-gradient: ${stroke(
            'var(--c-common-tertiary)',
            '#7b7b7b',
            'var(--c-nd-150)',
            '63%'
          )};
          --chip-surface-color: #0e0e0e;
          --chip-glow-color: rgba(193, 193, 193, 0.37);
        `;
      case 'transparent':
        return css`
          --border-gradient: none;
          --chip-surface-color: var(--c-nd-50);
          --chip-glow-color: transparent;
          backdrop-filter: blur(8px);
        `;
      case 'yellow':
        return css`
          --border-gradient: ${stroke(
            'var(--c-gold-400)',
            '#d4af37',
            'var(--c-gold-100)'
          )};
          --chip-surface-color: ${wash('var(--c-gold-100)')};
          --chip-glow-color: var(--c-gold-300);
        `;
      case 'orange':
        return css`
          --border-gradient: linear-gradient(
            323deg,
            var(--c-gold-200),
            var(--c-rewards-orange)
          );
          --chip-surface-color: ${wash('var(--c-gold-200)')};
          --chip-glow-color: var(--c-rewards-orange);
        `;
      case 'red':
        return css`
          --border-gradient: ${stroke(
            'var(--c-competition-laliga-tertiary)',
            'var(--c-modes-pro-tertiary)',
            '#ffe1b3'
          )};
          --chip-surface-color: ${wash('var(--c-modes-pro-tertiary)')};
          --chip-glow-color: var(--c-rare);
        `;
      case 'blue':
        return css`
          --border-gradient: ${stroke(
            'var(--c-competition-mls-tertiary)',
            '#0053f4',
            '#b3c5ff'
          )};
          --chip-surface-color: ${wash('var(--c-superRare)')};
          --chip-glow-color: var(--c-superRare);
        `;
      case 'green':
        return css`
          --border-gradient: ${stroke(
            'var(--c-competition-allStar-tertiary)',
            'var(--c-modes-classic-arena-tertiary)',
            '#b9ffb3'
          )};
          --chip-surface-color: ${wash('var(--c-green-400)')};
          --chip-glow-color: var(--c-modes-classic-arena-secondary);
        `;
      case 'purple':
        return css`
          --border-gradient: linear-gradient(
            229deg,
            var(--c-rewards-squad),
            #680099
          );
          --chip-surface-color: color-mix(
            in srgb,
            var(--c-squads-primary) 16%,
            rgba(14, 14, 14, 0.82)
          );
          --chip-glow-color: var(--c-squads-primary);
          backdrop-filter: blur(8px);
        `;
      case 'teal':
        return css`
          --border-gradient: ${stroke(
            'var(--c-competition-allStar-tertiary)',
            '#00a5ab',
            'var(--c-competition-allStar-primary)'
          )};
          --chip-surface-color: ${wash('var(--c-competition-allStar-primary)')};
          --chip-glow-color: var(--c-competition-allStar-primary);
        `;
      case 'light':
        return css`
          --border-gradient: ${stroke(
            'var(--c-dark-primary)',
            'var(--c-surface-light)',
            'var(--c-white)'
          )};
          --chip-surface-color:
            linear-gradient(
              to top,
              rgba(var(--c-rgb-white), 0.36) 0%,
              rgba(var(--c-rgb-white), 0.51) 42%,
              rgba(var(--c-rgb-white), 0.6) 80%
            ),
            color-mix(in srgb, var(--c-black) 15%, var(--c-white));
          --chip-glow-color: var(--c-white);
          --chip-text-color: var(--c-black);
        `;
      case 'glow':
        return css`
          --border-gradient: ${stroke('#2e1956', '#b3a9f4', 'var(--c-white)')};
          --chip-surface-color:
            linear-gradient(
              84.1deg,
              rgba(248, 211, 218, 0.5) 0%,
              rgba(179, 169, 244, 0.5) 28%,
              rgba(251, 233, 251, 0.5) 54%,
              rgba(79, 148, 253, 0.5) 100%
            ),
            var(--c-nd-100-opaque);
          --chip-glow-color: #b3a9f4;
        `;
      case 'default':
      default:
        return css`
          --border-gradient: ${stroke(
            'var(--c-common-tertiary)',
            'var(--c-nd-150)',
            'var(--c-white)',
            '63%'
          )};
          --chip-surface-color: var(--c-nd-100-opaque);
          --chip-glow-color: var(--c-nd-150);
          backdrop-filter: blur(8px);
        `;
    }
  }}
  ${({ size = 'M' }) => {
    switch (size) {
      case 'XS':
        return css`
          --border-size: 0.5px;
          --chip-glow-size: 4px;
          min-height: 12px;
          padding: 0 var(--half-unit);
          gap: 2px;
          font: 8px / 1 var(--brand-font);
        `;
      case 'S':
        return css`
          min-height: 20px;
          padding: 0 6px;
        `;
      case 'M':
      default:
        return css`
          min-height: 24px;
          padding: 0 var(--unit);
        `;
    }
  }}
`;
